/**
 * URL encodes a path component.
 * Use this to encode IDs and other dynamic path segments.
 *
 * @param value - The value to encode
 * @returns URL-encoded string
 *
 * @example
 * ```ts
 * enc('user@example.com') // 'user%40example.com'
 * enc('org-123') // 'org-123'
 * ```
 */
export function enc(value: string): string {
  return encodeURIComponent(value)
}

/**
 * Builds the base path for Pay API endpoints that require organization and connected account context.
 * Format: /api/pay/org/{orgId}/connected-account/{connectedAccountId}
 *
 * @param orgId - Organization ID
 * @param connectedAccountId - Connected account ID
 * @returns Base path string
 *
 * @example
 * ```ts
 * const basePath = payBase('org-123', 'ca-456')
 * // Returns: '/api/pay/org/org-123/connected-account/ca-456'
 *
 * // Combine with specific endpoint
 * const cardsUrl = `${basePath}/cards`
 * // Returns: '/api/pay/org/org-123/connected-account/ca-456/cards'
 * ```
 */
export function payBase(orgId: string, connectedAccountId: string): string {
  return `/api/pay/org/${enc(orgId)}/connected-account/${enc(connectedAccountId)}`
}

/**
 * Builds the base path for IAM API endpoints.
 * Format: /api/iam
 *
 * @returns Base path string
 *
 * @example
 * ```ts
 * const basePath = iamBase()
 * // Returns: '/api/iam'
 *
 * // Combine with specific endpoint
 * const meUrl = `${basePath}/me`
 * // Returns: '/api/iam/me'
 * ```
 */
export function iamBase(): string {
  return '/api/iam'
}

/**
 * Type-safe query parameter builder.
 * Filters out undefined values and builds a query string.
 *
 * @param params - Object with query parameters
 * @returns URLSearchParams instance
 *
 * @example
 * ```ts
 * const params = buildQueryParams({
 *   take: 10,
 *   skip: 0,
 *   search: undefined, // Will be filtered out
 * })
 * // Returns: URLSearchParams with 'take=10&skip=0'
 *
 * const url = `/cards?${params.toString()}`
 * ```
 */
export function buildQueryParams(params: Record<string, string | number | boolean | undefined>): URLSearchParams {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value))
    }
  })

  return searchParams
}
